import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../../Components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../Components/Navbars/DashboardNavbar";
import {
  Card,
  Grid,
  Tabs,
  Tab,
  Box,
  Chip,
  IconButton,
  Tooltip as MuiTooltip,
  Paper,
  Divider,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import PrintIcon from "@mui/icons-material/Print";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "../../Components/MDBox";
import MDTypography from "../../Components/MDTypography";
import MDButton from "../../Components/MDButton";
import MDInput from "../../Components/MDInput";
import MDDate from "../../Components/MDDate";
import AppLoading from "../../Components/MDLoading";
import {
  ManagementReportModel,
  IManagementSummary,
} from "../../Api/Models/ManagementReportModel";
import { ErrorToaast } from "../../utils/toast/Toast";

const COLORS = [
  "#1565C0",
  "#2E7D32",
  "#E65100",
  "#6A1B9A",
  "#C2185B",
  "#00838F",
  "#4E342E",
  "#D84315",
  "#283593",
  "#00695C",
];

interface ManagementDashboardProps {
  embedded?: boolean;
}

export default function ManagementDashboard({ embedded = false }: ManagementDashboardProps) {
  const model = useMemo(() => new ManagementReportModel(), []);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<IManagementSummary | null>(null);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0);

  const fetchData = async (fDate?: string, tDate?: string, search?: string) => {
    setLoading(true);
    try {
      const res = await model.getSummary(fDate || undefined, tDate || undefined, undefined, search || undefined);
      if (res.data && res.data.data) {
        setData(res.data.data);
      }
    } catch (err: any) {
      ErrorToaast("خطا در بارگذاری آمار مدیریتی");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(fromDate, toDate, searchTerm);
  }, []);

  const handlePrintStimulsoft = () => {
    const printUrl = model.getStimulsoftPrintUrl(fromDate, toDate, undefined, searchTerm);
    window.open(printUrl, "_blank");
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData(fromDate, toDate, searchTerm);
  };

  const handleApplyDateFilter = () => {
    fetchData(fromDate, toDate, searchTerm);
  };

  const handleResetDates = () => {
    setFromDate("");
    setToDate("");
    fetchData("", "", searchTerm);
  };

  const applyPreset = (from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
    fetchData(from, to, searchTerm);
  };

  // Prepare Chart Data
  const fleetPieData = useMemo(() => {
    if (!data?.vehicleTypeStats) return [];
    return data.vehicleTypeStats.slice(0, 6).map((v) => ({
      name: v.name,
      value: v.totalAmount,
      toman: v.totalAmountToman,
      count: v.count,
      percentage: v.percentage,
    }));
  }, [data]);

  const topTariffsData = useMemo(() => {
    if (!data?.tariffStats) return [];
    return data.tariffStats.slice(0, 7).map((t) => ({
      name: t.title.length > 25 ? t.title.substring(0, 22) + "..." : t.title,
      fullName: t.title,
      amount: t.totalAmountToman / 1_000_000, // Millions Toman
      count: t.count,
    }));
  }, [data]);

  const isPresetActive = (f: string, t: string) => {
    if (!f && !t) return !fromDate && !toDate;
    const fNorm = (fromDate || "").replace(/\//g, "-");
    const tNorm = (toDate || "").replace(/\//g, "-");
    return fNorm === f && tNorm === t;
  };

  const monthlyChartData = useMemo(() => {
    if (!data?.monthlyStats) return [];
    return data.monthlyStats.map((m) => ({
      name: m.monthName,
      incomeMillionToman: Math.round(m.totalAmountToman / 1_000_000),
      count: m.count,
    }));
  }, [data]);

  const content = (
    <MDBox pb={3}>
      {/* ================= HEADER BANNER ================= */}
      <Card
        sx={{
          p: 2.5,
          mb: 2.5,
          background: "linear-gradient(135deg, #0d47a1 0%, #1976d2 60%, #42a5f5 100%)",
          color: "white",
          boxShadow: "0 8px 24px rgba(13, 71, 161, 0.25)",
          borderRadius: "14px",
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={7}>
            <MDBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <AssessmentIcon sx={{ fontSize: 36, color: "#ffeb3b" }} />
              <MDTypography variant="h4" fontWeight="bold" color="white">
                {data?.organName || "سازمان حمل و نقل بانه"}
              </MDTypography>
            </MDBox>
            <MDTypography variant="body2" color="white" opacity={0.95} fontWeight="light">
              داشبورد جامع و گزارش رسمی مدیریتی عوارض خودرو و ناوگان حمل و نقل — بازه زمانی:{" "}
              <strong style={{ color: "#ffeb3b" }}>
                {data?.selectedPeriodTitle || "کل دوره‌ها (تجمیعی)"}
              </strong>{" "}
              | تاریخ گزارش: {data?.reportDateShamsi || "امروز"} | شماره گزارش:{" "}
              {data?.reportNumber || "---"}
            </MDTypography>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            display="flex"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
          >
            {/* Refresh button */}
            <MuiTooltip title="بروزرسانی داده‌ها">
              <IconButton
                onClick={() => fetchData(fromDate, toDate, searchTerm)}
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>

            {/* Print with Stimulsoft Button */}
            <MDButton
              variant="contained"
              onClick={handlePrintStimulsoft}
              sx={{
                bgcolor: "#ffb300",
                color: "#1a237e",
                fontWeight: "bold",
                fontSize: "13px",
                px: 2.5,
                py: 1,
                boxShadow: "0 4px 12px rgba(255, 179, 0, 0.4)",
                "&:hover": { bgcolor: "#ffa000", color: "#000" },
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PrintIcon />
              چاپ گزارش مدیریتی
            </MDButton>
          </Grid>
        </Grid>
      </Card>

      {/* ================= DATE RANGE FILTER CARD ================= */}
      <Card
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "14px",
          boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
          bgcolor: "#ffffff",
          border: "1px solid #e3f2fd",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Section title & badge */}
          <Grid item xs={12} md={3} display="flex" alignItems="center" gap={1.2}>
            <Box
              sx={{
                bgcolor: "#e3f2fd",
                color: "#1565c0",
                p: 1.2,
                borderRadius: "10px",
                display: "flex",
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <MDTypography variant="subtitle2" fontWeight="bold" color="dark">
                فیلتر بازه زمانی گزارش
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {data?.selectedPeriodTitle ? (
                  <span style={{ color: "#1565c0", fontWeight: "bold" }}>
                    {data.selectedPeriodTitle}
                  </span>
                ) : (
                  "مشاهده کلیه دوره‌ها و سال‌ها"
                )}
              </MDTypography>
            </Box>
          </Grid>

          {/* From Date */}
          <Grid item xs={12} sm={6} md={2.5}>
            <MDDate
              name="fromDate"
              label="از تاریخ (شروع بازه)"
              value={fromDate}
              defaultValue={fromDate}
              onChange={(val: any) => setFromDate(val || "")}
              portal
              size="small"
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={12} sm={6} md={2.5}>
            <MDDate
              name="toDate"
              label="تا تاریخ (پایان بازه)"
              value={toDate}
              defaultValue={toDate}
              onChange={(val: any) => setToDate(val || "")}
              portal
              size="small"
            />
          </Grid>

          {/* Filter Action Buttons */}
          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleApplyDateFilter}
              sx={{ px: 2, fontWeight: "bold" }}
            >
              <FilterListIcon sx={{ ml: 0.5, fontSize: 18 }} />
              اعمال فیلتر تاریخ
            </MDButton>

            {(fromDate || toDate) && (
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleResetDates}
                sx={{ px: 1.5 }}
              >
                <CloseIcon sx={{ ml: 0.5, fontSize: 16 }} />
                حذف فیلتر
              </MDButton>
            )}
          </Grid>

          {/* Quick Presets Chips */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" pt={0.5}>
              <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ ml: 0.5 }}>
                بازه زمانی آماده (سال ۱۴۰۵):
              </MDTypography>
              <Chip
                label="کل دوره‌ها (تجمیعی)"
                size="small"
                clickable
                color={isPresetActive("", "") ? "primary" : "default"}
                variant={isPresetActive("", "") ? "filled" : "outlined"}
                onClick={() => applyPreset("", "")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="امسال (۱۴۰۵)"
                size="small"
                clickable
                color={isPresetActive("1405-01-01", "1405-12-29") ? "primary" : "default"}
                variant={isPresetActive("1405-01-01", "1405-12-29") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-01-01", "1405-12-29")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="۶ ماهه اول ۱۴۰۵"
                size="small"
                clickable
                color={isPresetActive("1405-01-01", "1405-06-31") ? "primary" : "default"}
                variant={isPresetActive("1405-01-01", "1405-06-31") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-01-01", "1405-06-31")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="۶ ماهه دوم ۱۴۰۵"
                size="small"
                clickable
                color={isPresetActive("1405-07-01", "1405-12-29") ? "primary" : "default"}
                variant={isPresetActive("1405-07-01", "1405-12-29") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-07-01", "1405-12-29")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="فصل پاییز ۱۴۰۵"
                size="small"
                clickable
                color={isPresetActive("1405-07-01", "1405-09-30") ? "primary" : "default"}
                variant={isPresetActive("1405-07-01", "1405-09-30") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-07-01", "1405-09-30")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="فصل تابستان ۱۴۰۵"
                size="small"
                clickable
                color={isPresetActive("1405-04-01", "1405-06-31") ? "primary" : "default"}
                variant={isPresetActive("1405-04-01", "1405-06-31") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-04-01", "1405-06-31")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="فصل بهار ۱۴۰۵"
                size="small"
                clickable
                color={isPresetActive("1405-01-01", "1405-03-31") ? "primary" : "default"}
                variant={isPresetActive("1405-01-01", "1405-03-31") ? "filled" : "outlined"}
                onClick={() => applyPreset("1405-01-01", "1405-03-31")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
              <Chip
                label="سال مالی ۱۴۰۴"
                size="small"
                clickable
                color={isPresetActive("1404-01-01", "1404-12-29") ? "primary" : "default"}
                variant={isPresetActive("1404-01-01", "1404-12-29") ? "filled" : "outlined"}
                onClick={() => applyPreset("1404-01-01", "1404-12-29")}
                sx={{ fontWeight: "bold", fontSize: "12px" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* ================= 5 KPI SUMMARY CARDS ================= */}
      <Grid container spacing={2.5} mb={3}>
        {/* Total Income */}
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              borderTop: "4px solid #2e7d32",
              height: "100%",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  مجموع درآمد کل (وصولی)
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" color="success" mt={0.5}>
                  {data?.totalAmountToman?.toLocaleString("fa-IR") || "۰"}{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>تومان</span>
                </MDTypography>
                <MDTypography variant="caption" color="text" fontSize="11px">
                  {data?.totalAmount?.toLocaleString("fa-IR") || "۰"} ریال
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  p: 1.2,
                  borderRadius: "10px",
                  display: "flex",
                }}
              >
                <MonetizationOnIcon sx={{ fontSize: 30 }} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Total Bills */}
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              borderTop: "4px solid #1976d2",
              height: "100%",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  تعداد کل قبوض صادرشده
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" color="info" mt={0.5}>
                  {data?.totalBills?.toLocaleString("fa-IR") || "۰"}{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>فقره</span>
                </MDTypography>
                <MDTypography variant="caption" color="text" fontSize="11px">
                  پرداخت‌شده: {data?.paidBillsCount?.toLocaleString("fa-IR") || "۰"}
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                  p: 1.2,
                  borderRadius: "10px",
                  display: "flex",
                }}
              >
                <ReceiptLongIcon sx={{ fontSize: 30 }} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Collection Rate */}
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              borderTop: "4px solid #00838f",
              height: "100%",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  درصد وصول و تسویه
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" color="primary" mt={0.5}>
                  {data?.collectionRate?.toLocaleString("fa-IR") || "۱۰۰"} %
                </MDTypography>
                <MDTypography variant="caption" color="success" fontSize="11px" fontWeight="bold">
                  وضعیت وصولی کامل
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  bgcolor: "#e0f7fa",
                  color: "#00838f",
                  p: 1.2,
                  borderRadius: "10px",
                  display: "flex",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 30 }} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Average Bill Amount */}
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              borderTop: "4px solid #f57c00",
              height: "100%",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  میانگین هر فیش صادره
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" color="warning" mt={0.5}>
                  {Math.round((data?.averageBillAmount || 0) / 10).toLocaleString("fa-IR")}{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>تومان</span>
                </MDTypography>
                <MDTypography variant="caption" color="text" fontSize="11px">
                  {data?.averageBillAmount?.toLocaleString("fa-IR") || "۰"} ریال
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  bgcolor: "#fff3e0",
                  color: "#f57c00",
                  p: 1.2,
                  borderRadius: "10px",
                  display: "flex",
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 30 }} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Fleet & Services Count */}
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              borderTop: "4px solid #6a1b9a",
              height: "100%",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  اقلام تعرفه و ناوگان
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#6a1b9a" }} mt={0.5}>
                  {data?.totalBillItems?.toLocaleString("fa-IR") || "۰"}{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>خدمت</span>
                </MDTypography>
                <MDTypography variant="caption" color="text" fontSize="11px">
                  {data?.activeVehiclesCount || 0} گروه ناوگان خودرویی
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  bgcolor: "#f3e5f5",
                  color: "#6a1b9a",
                  p: 1.2,
                  borderRadius: "10px",
                  display: "flex",
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 30 }} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* ================= INTERACTIVE CHARTS ROW ================= */}
      <Grid container spacing={2.5} mb={3}>
        {/* Chart 1: Fleet Distribution (Pie/Donut) */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2.5, borderRadius: "14px", height: 380, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <LocalTaxiIcon color="primary" />
                <MDTypography variant="h6" fontWeight="bold">
                  سهم درآمد به تفکیک ناوگان و نوع خودرو
                </MDTypography>
              </MDBox>
              <Chip label="تحلیل سهم درآمد" size="small" color="info" variant="outlined" />
            </MDBox>
            <Divider sx={{ my: 1 }} />
            <ResponsiveContainer width="100%" height={290}>
              <PieChart>
                <Pie
                  data={fleetPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                >
                  {fleetPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => [
                    `${(Math.round(value / 10)).toLocaleString("fa-IR")} تومان`,
                    "مجموع درآمد",
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Chart 2: Top Tariffs (Bar Chart) */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2.5, borderRadius: "14px", height: 380, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <TrendingUpIcon color="success" />
                <MDTypography variant="h6" fontWeight="bold">
                  سرفصل‌های برتر درآمدی تعرفه‌ها (میلیون تومان)
                </MDTypography>
              </MDBox>
              <Chip label="۷ تعرفه برتر" size="small" color="success" variant="outlined" />
            </MDBox>
            <Divider sx={{ my: 1 }} />
            <ResponsiveContainer width="100%" height={290}>
              <BarChart
                data={topTariffsData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="name" type="category" width={140} fontSize={10} />
                <Tooltip
                  formatter={(val: any) => [`${val.toLocaleString("fa-IR")} میلیون تومان`, "درآمد"]}
                  labelFormatter={(name) => {
                    const found = topTariffsData.find((x) => x.name === name);
                    return found?.fullName || name;
                  }}
                />
                <Bar dataKey="amount" fill="#1565C0" radius={[0, 6, 6, 0]}>
                  {topTariffsData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Chart 3: Monthly Trend (Area Chart) */}
        <Grid item xs={12} lg={12}>
          <Card sx={{ p: 2.5, borderRadius: "14px", height: 330, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <AssessmentIcon color="info" />
                <MDTypography variant="h6" fontWeight="bold">
                  روند ماهانه وصول عوارض ناوگان (میلیون تومان)
                </MDTypography>
              </MDBox>
              <Chip label="سیر زمانی درآمدهای وصول‌شده" size="small" color="primary" variant="outlined" />
            </MDBox>
            <Divider sx={{ my: 1 }} />
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={monthlyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1565C0" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#1565C0" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip
                  formatter={(val: any, name: any) => [
                    name === "incomeMillionToman"
                      ? `${val.toLocaleString("fa-IR")} میلیون تومان`
                      : `${val.toLocaleString("fa-IR")} فقره`,
                    name === "incomeMillionToman" ? "درآمد وصولی" : "تعداد فیش",
                  ]}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="incomeMillionToman"
                  name="درآمد وصولی (میلیون تومان)"
                  stroke="#1565C0"
                  fillOpacity={1}
                  fill="url(#incomeGradient)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* ================= DETAILED TABBED TABLES ================= */}
      <Card sx={{ borderRadius: "14px", boxShadow: "0 4px 18px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8f9fa", px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="۱. تفکیک عملکرد ناوگان و انواع خودرو" sx={{ fontWeight: "bold", fontSize: "14px" }} />
            <Tab label="۲. تحلیل سرفصل‌های تعرفه و خدمات" sx={{ fontWeight: "bold", fontSize: "14px" }} />
            <Tab label="۳. خلاصه و ریز آخرین قبوض صادره" sx={{ fontWeight: "bold", fontSize: "14px" }} />
          </Tabs>
        </Box>

        {/* TAB 1: FLEET & VEHICLE TYPES */}
        {activeTab === 0 && (
          <Box p={2.5}>
            <MDTypography variant="h6" fontWeight="bold" mb={1.5} color="primary">
              گزارش عملکرد وصول عوارض به تفکیک ناوگان و انواع خودرو
            </MDTypography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#37474f", color: "#ffffff", height: "42px" }}>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>ردیف</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64", textAlign: "right" }}>نوع خودرو و ناوگان حمل و نقل</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>تعداد خدمات / فیش‌ها</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>مبلغ کل درآمد (ریال)</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>مبلغ کل (تومان)</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>سهم از کل درآمد</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.vehicleTypeStats?.map((v, idx) => (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff",
                        height: "38px",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <td style={{ padding: "8px" }}>{v.row}</td>
                      <td style={{ padding: "8px", textAlign: "right", fontWeight: "bold" }}>{v.name}</td>
                      <td style={{ padding: "8px" }}>{v.count.toLocaleString("fa-IR")}</td>
                      <td style={{ padding: "8px", fontWeight: "bold", color: "#1565C0" }}>
                        {v.totalAmount.toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "8px", color: "#2e7d32", fontWeight: "bold" }}>
                        {v.totalAmountToman.toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "8px" }}>
                        <Chip
                          label={`${v.percentage}%`}
                          size="small"
                          sx={{
                            bgcolor: idx < 3 ? "#e3f2fd" : "#f5f5f5",
                            color: idx < 3 ? "#0d47a1" : "#424242",
                            fontWeight: "bold",
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr style={{ backgroundColor: "#eceff1", fontWeight: "bold", height: "44px" }}>
                    <td colSpan={2} style={{ padding: "8px", textAlign: "right" }}>
                      جمع کل ناوگان حمل و نقل
                    </td>
                    <td style={{ padding: "8px" }}>{data?.vehicleTypeStats?.reduce((a, b) => a + b.count, 0).toLocaleString("fa-IR")}</td>
                    <td style={{ padding: "8px", color: "#1565C0" }}>{data?.totalAmount?.toLocaleString("fa-IR")}</td>
                    <td style={{ padding: "8px", color: "#2e7d32" }}>{data?.totalAmountToman?.toLocaleString("fa-IR")}</td>
                    <td style={{ padding: "8px" }}>۱۰۰ %</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {/* TAB 2: TARIFFS BREAKDOWN */}
        {activeTab === 1 && (
          <Box p={2.5}>
            <MDTypography variant="h6" fontWeight="bold" mb={1.5} color="primary">
              تحلیل عملکرد درآمدی به تفکیک کد و عناوین تعرفه‌های مصوب
            </MDTypography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#37474f", color: "#ffffff", height: "42px" }}>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>ردیف</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>کد عوارض</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64", textAlign: "right" }}>عنوان تعرفه مصوب</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>تعداد دفعات وصول</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>مبلغ کل وصولی (ریال)</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>مبلغ به تومان</th>
                    <th style={{ padding: "8px", border: "1px solid #455a64" }}>سهم از درآمد</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.tariffStats?.map((t, idx) => (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff",
                        height: "38px",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <td style={{ padding: "8px" }}>{t.row}</td>
                      <td style={{ padding: "8px", fontFamily: "monospace" }}>{t.avarezCode || "---"}</td>
                      <td style={{ padding: "8px", textAlign: "right", fontWeight: "bold" }}>{t.title}</td>
                      <td style={{ padding: "8px" }}>{t.count.toLocaleString("fa-IR")}</td>
                      <td style={{ padding: "8px", fontWeight: "bold", color: "#1565C0" }}>
                        {t.totalAmount.toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "8px", color: "#2e7d32", fontWeight: "bold" }}>
                        {t.totalAmountToman.toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "8px" }}>
                        <Chip
                          label={`${t.percentage}%`}
                          size="small"
                          sx={{
                            bgcolor: idx < 5 ? "#e8f5e9" : "#f5f5f5",
                            color: idx < 5 ? "#1b5e20" : "#424242",
                            fontWeight: "bold",
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {/* TAB 3: RECENT BILLS LIST */}
        {activeTab === 2 && (
          <Box p={2.5}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={2}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                ریز پرونده‌ها و آخرین تراکنش‌های فیش‌های صادره (۵۰ فیش اخیر)
              </MDTypography>
              <Box component="form" onSubmit={handleSearchSubmit} display="flex" gap={1}>
                <input
                  type="text"
                  placeholder="جستجو نام متقاضی، پلاک، کدملی یا تاکسی..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "13px",
                    minWidth: "250px",
                  }}
                />
                <MDButton size="small" variant="outlined" color="info" type="submit">
                  جستجو
                </MDButton>
              </Box>
            </MDBox>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center", fontSize: "12.5px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#37474f", color: "#ffffff", height: "40px" }}>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>کد قبض</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>شماره فیش</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>سال</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64", textAlign: "right" }}>نام متقاضی</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>کد ملی</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>شماره پلاک</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>کد تاکسی / خط</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>مبلغ کل (ریال)</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>وضعیت</th>
                    <th style={{ padding: "6px", border: "1px solid #455a64" }}>تاریخ پرداخت</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recentBills?.map((b, idx) => (
                    <tr
                      key={b.id}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff",
                        height: "36px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <td style={{ padding: "6px", fontWeight: "bold" }}>{b.code}</td>
                      <td style={{ padding: "6px", fontFamily: "monospace" }}>{b.billNumber}</td>
                      <td style={{ padding: "6px" }}>{b.year}</td>
                      <td style={{ padding: "6px", textAlign: "right", fontWeight: "bold" }}>{b.motaghazi}</td>
                      <td style={{ padding: "6px", fontFamily: "monospace" }}>{b.nationalCode}</td>
                      <td style={{ padding: "6px", direction: "ltr" }}>{b.plateNumber}</td>
                      <td style={{ padding: "6px" }}>{b.taxiCode}</td>
                      <td style={{ padding: "6px", fontWeight: "bold", color: "#1565C0" }}>
                        {b.totalAmount.toLocaleString("fa-IR")}
                      </td>
                      <td style={{ padding: "6px" }}>
                        <Chip
                          label={b.isPaid ? "پرداخت شده" : "پرداخت نشده"}
                          size="small"
                          color={b.isPaid ? "success" : "error"}
                          sx={{ height: 22, fontSize: 11 }}
                        />
                      </td>
                      <td style={{ padding: "6px", fontSize: 11 }}>{b.paidDateShamsi || b.issueDateShamsi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
      </Card>

      {/* ================= BANK & SIGNATURE FOOTER ================= */}
      <Card sx={{ p: 2.5, mt: 3, borderRadius: "14px", bgcolor: "#f1f8e9", border: "1px solid #c8e6c9" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <AccountBalanceIcon sx={{ color: "#2e7d32", fontSize: 28 }} />
              <MDBox>
                <MDTypography variant="subtitle2" fontWeight="bold" color="success">
                  اطلاعات حساب بانکی و واریز عوارض سازمان:
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  بانک: {data?.bankInfo?.bankName || "بانک رفاه کارگران مرکزی"} | شماره حساب:{" "}
                  <strong>{data?.bankInfo?.accountNumber || "421583961"}</strong> | مدیریت:{" "}
                  {data?.bankInfo?.managerName || "سیامند اسراری"} | نشانی:{" "}
                  {data?.bankInfo?.address || "بانه، خیابان انقلاب"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={5} display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>

          </Grid>
        </Grid>
      </Card>

      <AppLoading show={loading} />
    </MDBox>
  );

  if (embedded) {
    return content;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar name={"گزارش مدیریتی سازمان حمل و نقل بانه"} />
      {content}
    </DashboardLayout>
  );
}
